import { supportMenu } from '@utils/Index';

import { formatDate } from '@utils/Date';

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
  doc,
  getDoc,
  addDoc,
  onSnapshot,
  deleteDoc,
  setDoc,
  deleteField,
} from 'firebase/firestore';
import { db } from '@root/firebase';
import { memorialColors } from '@utils/MemorialFunctions';
import { toast } from 'react-hot-toast';
import Comments from '@components/Comments';
import LikeButton from '@components/LikeButton';
import FavoriteButton from '@components/FavoriteButton';
import ReadMoreText from '@components/ReadMoreText';

const LIMIT = 3; // Number of tributes per page

function CommunitySupport() {
  const { globalUser, defaultAvatarUrl } = useAuth();

  const [tributes, setTributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [comments, setComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [likedTributes, setLikedTributes] = useState({});
  const [favTributes, setFavTributes] = useState({});

  useEffect(() => {
    const unsubscribeMap = {};

    tributes.forEach((tribute) => {
      unsubscribeMap[tribute.id] = fetchComments(tribute.id);
    });

    return () => {
      // Unsubscribe from all listeners when the component unmounts
      Object.values(unsubscribeMap).forEach((unsubscribe) => unsubscribe());
    };
  }, [tributes]);

  const rateLimit = async (tasks, limit = 5) => {
    const results = [];
    const executing = [];

    for (const task of tasks) {
      const p = Promise.resolve(task());
      results.push(p);
      executing.push(p);
      if (executing.length >= limit) {
        await Promise.race(executing);
        executing.splice(executing.indexOf(await Promise.race(executing)), 1);
      }
    }

    return Promise.all(results);
  };

  const fetchComments = (tributeId) => {
    const commentsRef = collection(db, `memorials/${tributeId}/comments`);
    const commentsQuery = query(commentsRef, orderBy('createdDate', 'asc'));

    setLoadingComments((prev) => ({ ...prev, [tributeId]: true }));

    const unsubscribe = onSnapshot(commentsQuery, async (snapshot) => {
      const tasks = snapshot.docs.map((docSnapshot) => async () => {
        const commentData = { id: docSnapshot.id, ...docSnapshot.data() };

        if (!commentData.uid) {
          return {
            ...commentData,
            user: {
              avatar: defaultAvatarUrl,
              name: 'Unknown User',
            },
          };
        }

        try {
          const personalDetailsRef = doc(db, 'users', commentData.uid);
          const personalDetailsDoc = await getDoc(personalDetailsRef);

          if (personalDetailsDoc.exists()) {
            const userDetails = personalDetailsDoc.data();
            return {
              ...commentData,
              user: {
                avatar: userDetails.avatar || defaultAvatarUrl,
                name: userDetails.name || 'Unknown User',
              },
            };
          }
        } catch (error) {
          console.error(
            `Error fetching user data for UID: ${commentData.uid}`,
            error
          );
        }

        return {
          ...commentData,
          user: {
            avatar: defaultAvatarUrl,
            name: 'Unknown User',
          },
        };
      });

      const tributeComments = await rateLimit(tasks, 5);

      setComments((prev) => ({ ...prev, [tributeId]: tributeComments }));
      setLoadingComments((prev) => ({ ...prev, [tributeId]: false }));
    });

    return unsubscribe;
  };

  // Fetch Tributes with Pagination
  const toggleAction = async (
    tributeId,
    actionType,
    currentState,
    stateSetter
  ) => {
    try {
      const actionRef = doc(
        db,
        `memorials/${tributeId}/${actionType}`,
        globalUser.uid
      );

      if (currentState[tributeId]) {
        await deleteDoc(actionRef);
        stateSetter((prev) => ({ ...prev, [tributeId]: false }));

        toast.success(
          `${actionType === 'liked' ? 'Unliked' : 'Unfavorited'} successfully!`
        );
      } else {
        await setDoc(actionRef, {
          userId: globalUser.uid,
          tributeId,
          action: actionType,
          timestamp: new Date(),
        });
        stateSetter((prev) => ({ ...prev, [tributeId]: true }));

        toast.success(
          `${actionType === 'liked' ? 'Liked' : 'Favorited'} successfully!`
        );
      }
    } catch (error) {
      console.error(
        `Error toggling ${actionType} action for tribute ${tributeId}:`,
        error
      );
      toast.error(`Failed to toggle ${actionType}.`);
    }
  };

  const fetchTributes = async (isLoadMore = false) => {
    setLoading(true);
    try {
      const memorialsRef = collection(db, 'memorials');
      const tributesQuery = query(
        memorialsRef,
        where('privacy', '==', 'Public'),
        orderBy('createdDate', 'desc'),
        ...(isLoadMore && lastVisible ? [startAfter(lastVisible)] : []),
        limit(LIMIT)
      );

      const querySnapshot = await getDocs(tributesQuery);
      const newTributes = await Promise.all(
        querySnapshot.docs.map(async (docSnapshot) => {
          const tributeData = { id: docSnapshot.id, ...docSnapshot.data() };

          if (tributeData.uid) {
            const userDoc = await getDoc(doc(db, 'users', tributeData.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              return {
                ...tributeData,
                userDetails: {
                  avatar: userData.avatar || defaultAvatarUrl,
                  name: userData.name || 'Unknown User',
                },
              };
            }
          }
          return {
            ...tributeData,
            userDetails: {
              avatar: defaultAvatarUrl,
              name: 'Unknown User',
            },
          };
        })
      );

      setTributes((prev) =>
        isLoadMore ? [...prev, ...newTributes] : newTributes
      );
      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setHasMore(querySnapshot.docs.length === LIMIT);
    } catch (error) {
      console.error('Error fetching tributes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTributes();
  }, []);

  // Load More Handler
  const handleLoadMore = () => {
    fetchTributes(true);
  };

  const renderSupportMenuItems = (menu) =>
    menu.map((item, index) => (
      <li key={index}>
        <a href='#' className='d-flex align-items-center loading-icon-trigger'>
          <i className={item.icon}></i>
          <span className='me-auto'>{item.value}</span>
        </a>
      </li>
    ));

  const addComment = async (tributeId, commentText) => {
    if (!commentText.trim()) return;

    try {
      const newComment = {
        text: commentText,
        createdDate: new Date(),
        uid: globalUser.uid,
      };

      const commentsRef = collection(db, `memorials/${tributeId}/comments`);
      await addDoc(commentsRef, newComment);

      setComments((prev) => ({
        ...prev,
        [tributeId]: [
          ...(prev[tributeId] || []),
          { ...newComment, id: new Date().getTime() },
        ],
      }));
    } catch (error) {
      console.error(`Error adding comment to tribute ${tributeId}:`, error);
    }
  };

  return (
    <>
      <div className='col-lg-6'>
        <div className='col-lg-12'>
          <div className='card'>
            <div className='card-header'>
              <h5 className='card-title mb-0'>Community Forum and Support</h5>
              <p>
                <strong>Tip:</strong> You can browse public memorials, events,
                or support messages.
              </p>
            </div>
          </div>
        </div>

        {loading && tributes.length === 0 ? (
          <p>Loading...</p>
        ) : tributes.length > 0 ? (
          tributes.map((tribute) => (
            <div key={tribute.id} className='card mb-3'>
              <div className='card-body'>
                {/* User Info Section */}
                <div className='d-flex justify-content-between align-items-center position-relative'>
                  {/* Left-Aligned Main Title */}
                  <div className='d-flex align-items-center mb-0'>
                    <img
                      src={tribute.userDetails?.avatar || defaultAvatarUrl}
                      alt='User Avatar'
                      className='avatar-sm rounded-circle img-thumbnail me-3'
                    />
                    <div>
                      <h5 className='card-title mb-0'>
                        {tribute.userDetails?.name || 'User'}
                      </h5>
                      <small className='text-muted'>
                        {tribute.createdDate?.toDate
                          ? formatDate(tribute.createdDate.toDate(), 'long')
                          : 'Date not available'}
                      </small>
                    </div>
                  </div>
                  {/* Right-Aligned Icons */}
                  <div className='text-end'>
                    <span
                      className={`badge badge-soft-${memorialColors(tribute.tribute)} mb-0`}
                    >
                      {tribute.tribute || 'No Tribute'}
                    </span>
                  </div>
                </div>

                {/* Tribute Image and Title */}
                <div className='vstack gap-2'>
                  <div className='grid-example bg-white'>
                    <div className='d-flex align-items-center mb-2'>
                      <img
                        src={tribute.image || '/default-image.jpg'}
                        alt='Tribute'
                        className='img-fluid rounded'
                        style={{ width: '100%', objectFit: 'cover' }}
                      />
                    </div>

                    {/* Title and Actions */}
                    <div className='d-flex justify-content-between align-items-center position-relative mb-2'>
                      {/* Left-Aligned Main Title */}
                      <h5
                        className={`text-${memorialColors(tribute.tribute)} mb-0`}
                      >
                        {tribute.mainTitle || 'Untitled'}
                      </h5>

                      {/* Right-Aligned Icons */}
                      <div className='text-end'>
                        <LikeButton
                          collectionPath='memorials'
                          documentId={tribute.id}
                          userId={globalUser.uid}
                        />
                        <FavoriteButton
                          collectionPath='memorials'
                          documentId={tribute.id}
                          userId={globalUser.uid}
                        />
                      </div>
                    </div>

                    <h6>{tribute.subTitle || 'Untitled'}</h6>

                    {/* Subtitle and Repertoire */}
                    {tribute.tribute === 'Event' && (
                      <>
                        <hr />
                        <div className='row mb-2'>
                          <div className='col-2'>
                            <strong>When:</strong>
                          </div>
                          <div className='col-4'>
                            {tribute.eventDate?.toDate
                              ? formatDate(tribute.eventDate.toDate())
                              : 'Date not available'}
                          </div>
                          <div className='col-2'>
                            <strong>Time:</strong>
                          </div>
                          <div className='col-4'>
                            {tribute?.eventTime || 'Time not provided'}
                          </div>
                        </div>
                        <div className='row'>
                          <div className='col-2'>
                            <strong>Address:</strong>
                          </div>
                          <div className='col-10'>
                            {tribute.address?.city || 'City not provided'},{' '}
                            {tribute.address?.zip || 'ZIP not provided'}
                          </div>
                        </div>
                      </>
                    )}

                    <ReadMoreText
                      text={tribute.message || 'No Message to Show'}
                      wordLimit={1}
                      itemId={tribute.id} // Unique ID for tracking
                      userId={globalUser.uid} // Logged-in user ID
                    />
                  </div>
                </div>

                <hr />

                {/* Comments Section (Accordion inside `Comments.jsx`) */}
                <Comments
                  parentId={tribute.id}
                  collectionPath='memorials'
                  globalUser={globalUser}
                  defaultAvatarUrl={defaultAvatarUrl}
                  colorClass={memorialColors(tribute.tribute)}
                />
              </div>
            </div>
          ))
        ) : (
          <p>No tributes found.</p>
        )}

        {/* Load More Button */}
        {hasMore && !loading && (
          <div className='text-center mt-3'>
            <button className='btn btn-primary' onClick={handleLoadMore}>
              Load More
            </button>
          </div>
        )}
      </div>

      <div className='col-lg-3'>
        <div className='card'>
          <h5 className='card-header bg-transparent border-bottom text-uppercase'>
            Menu Items
          </h5>
          <div className='card-body'>
            <ul className='list-unstyled categories-list'>
              <li>
                <div className='custom-accordion'>
                  <a
                    className='text-body fw-medium py-1 d-flex align-items-center'
                    data-bs-toggle='collapse'
                    href='#occasion-healing'
                    role='button'
                    aria-expanded='false'
                    aria-controls='occasion-collapse'
                  >
                    <i className='mdi mdi-folder font-size-20 text-warning me-2'></i>{' '}
                    Guided Healing{' '}
                    <i className='mdi mdi-chevron-up accor-down-icon ms-auto'></i>
                  </a>
                  <div className='collapse' id='occasion-healing'>
                    <div className='card border-0 shadow-none ps-2 mb-0'>
                      <ul className='list-unstyled mb-0'>
                        {renderSupportMenuItems(supportMenu)}
                      </ul>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

export default CommunitySupport;
